import { useSearchParams } from "react-router-dom";
import { useFetch } from "@/lib/useFetch";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Award, Building2 } from "lucide-react";
import { fmtDate } from "@/lib/utils";

export default function VerifyCert() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { data, loading } = useFetch(() => token ? api.verifyCert(token) : Promise.resolve({ valid: false, message: "No token provided" }), [token]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="orb bg-primary/20 absolute -top-20 -left-20 w-80 h-80 pointer-events-none" />
      <div className="orb bg-cyan-400/10 absolute bottom-0 right-0 w-96 h-96 pointer-events-none" style={{ animationDelay: "8s" }} />

      <div className="relative z-10 w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <div className="w-12 h-12 rounded-2xl bg-primary flex items-center justify-center mb-3 shadow-lg shadow-primary/30">
            <Building2 className="h-6 w-6 text-white" />
          </div>
          <h1 className="text-xl font-bold">Certificate Verification</h1>
          <p className="text-sm text-muted-foreground mt-1">BMS Institute of Technology & Management</p>
        </div>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl p-7">
          {loading ? (
            <div className="flex flex-col items-center gap-3 py-8">
              <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
              <p className="text-sm text-muted-foreground">Verifying…</p>
            </div>
          ) : data?.valid ? (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-950/40 flex items-center justify-center mx-auto">
                <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-green-600 dark:text-green-400 mb-1">Valid Certificate</div>
                <h2 className="text-2xl font-bold">{data.participant}</h2>
                <p className="text-sm text-muted-foreground mt-1">{data.eventTitle}</p>
              </div>
              <div className="bg-green-50 dark:bg-green-950/20 rounded-xl p-4 text-sm space-y-1.5 text-left">
                {data.certID   && <div className="flex justify-between"><span className="text-muted-foreground">Certificate ID</span><span className="font-mono font-medium">{data.certID}</span></div>}
                {data.usn      && <div className="flex justify-between"><span className="text-muted-foreground">USN</span><span className="font-medium">{data.usn}</span></div>}
                {data.eventDate && <div className="flex justify-between"><span className="text-muted-foreground">Event date</span><span className="font-medium">{fmtDate(data.eventDate)}</span></div>}
                {data.issuedAt && <div className="flex justify-between"><span className="text-muted-foreground">Issued</span><span className="font-medium">{fmtDate(data.issuedAt)}</span></div>}
              </div>
              <p className="text-[11px] text-muted-foreground">This certificate was issued by {`BMS Institute of Technology & Management`} and is cryptographically verified.</p>
            </div>
          ) : (
            <div className="text-center space-y-4 py-4">
              <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-950/40 flex items-center justify-center mx-auto">
                {data?.revoked ? <Award className="h-8 w-8 text-red-500" /> : <XCircle className="h-8 w-8 text-red-500" />}
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-red-500 mb-1">
                  {data?.revoked ? "Certificate Revoked" : "Invalid Certificate"}
                </div>
                <p className="text-sm text-muted-foreground">{data?.message || "This certificate could not be verified."}</p>
              </div>
              {!token && <p className="text-xs text-muted-foreground">No verification token was provided in the URL.</p>}
            </div>
          )}
        </motion.div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Powered by <span className="font-semibold text-primary">Fortifyhub.net</span> · Arventiq Labs
        </p>
      </div>
    </div>
  );
}
